import { create } from "zustand";
import { McpChangesetParams, McpConnectionInfo } from "@/types/mcpTypes";
import { FusaReply } from "@/types/replyTypes";
import { McpFrontendController } from "../controllers/mcpController";

type ChangesetScope = 'open' | 'recent';

interface McpState {
  changesets: McpChangesetParams[];
  changesetScope: ChangesetScope;
  /** How to connect an MCP client to this deployment; null until fetched. */
  connection: McpConnectionInfo | null;
  connectionLoadFailed: boolean;
}

const initialState: McpState = {
  changesets: [],
  changesetScope: 'open',
  connection: null,
  connectionLoadFailed: false,
};

export interface McpStore extends McpState {
  fetchConnectionInfo: () => Promise<void>;
  /** Resolves false when the request failed (the client has already shown why). */
  fetchChangesets: (scope?: ChangesetScope) => Promise<boolean>;
  /** Refreshes one changeset already in the list, in place. Resolves false when the request failed. */
  fetchChangeset: (changesetId: string) => Promise<boolean>;
  approve: (changesetId: string, actionIds: string[]) => Promise<void>;
  reject: (changesetId: string, actionIds: string[]) => Promise<void>;
  reset: () => void;
}

/**
 * When the server last changed anything in the changeset. Every status change,
 * and every heartbeat of a running batch, bumps an action's updatedAt.
 */
const lastChangedAt = (changeset: McpChangesetParams) =>
  Math.max(new Date(changeset.updatedAt).getTime(), ...changeset.actions.map((action) => new Date(action.updatedAt).getTime()));

/**
 * Polls, approvals and rejections overlap, and a response can arrive after one
 * that reflects a later state (a poll sent before an approval, answered after
 * it). Keep whichever copy the server changed most recently, so an approved
 * action never shows as pending again.
 */
const newest = (incoming: McpChangesetParams, existing: McpChangesetParams | undefined) =>
  existing && lastChangedAt(existing) > lastChangedAt(incoming) ? existing : incoming;

/** A request that threw (no session, say) is a failed request like any other. */
const send = async <T extends FusaReply>(request: () => Promise<T>): Promise<T | null> => {
  try {
    const response = await request();
    return response.errors ? null : response;
  } catch {
    return null;
  }
};

export const useMcpStore = create<McpStore>((set, get) => {
  // Polls and tab switches overlap; only the most recently started fetch may
  // write the list, so a slow poll for the old tab cannot land after a switch.
  let latestChangesetFetch = 0;

  /** Swaps a changeset in place, so the rest of the list keeps its order and selection. */
  const replaceChangeset = (changeset: McpChangesetParams) => set({
    changesets: get().changesets.map((existing) => (existing.id === changeset.id ? newest(changeset, existing) : existing)),
  });

  const decide = async (decision: 'approve' | 'reject', changesetId: string, actionIds: string[]) => {
    const response = await send(() => new McpFrontendController()[decision](changesetId, actionIds));
    if (response) {
      replaceChangeset(response.changeset);
    }
  };

  return {
    ...initialState,

    fetchConnectionInfo: async () => {
      set({ connectionLoadFailed: false });
      const response = await send(() => new McpFrontendController().readConnectionInfo());
      set(response ? { connection: response.connection, connectionLoadFailed: false } : { connectionLoadFailed: true });
    },

    fetchChangesets: async (scope) => {
      const nextScope = scope ?? get().changesetScope;
      const fetchId = ++latestChangesetFetch;
      if (scope) {
        set({ changesetScope: scope });
      }
      const response = await send(() => new McpFrontendController().readChangesets(nextScope));
      if (response && fetchId === latestChangesetFetch) {
        const current = new Map(get().changesets.map((changeset) => [changeset.id, changeset]));
        set({ changesets: response.changesets.map((changeset) => newest(changeset, current.get(changeset.id))) });
      }
      return response !== null;
    },

    fetchChangeset: async (changesetId) => {
      const response = await send(() => new McpFrontendController().readChangeset(changesetId));
      if (response) {
        replaceChangeset(response.changeset);
      }
      return response !== null;
    },

    approve: (changesetId, actionIds) => decide('approve', changesetId, actionIds),
    reject: (changesetId, actionIds) => decide('reject', changesetId, actionIds),

    reset: () => {
      latestChangesetFetch += 1;
      set({ ...initialState });
    },
  };
});
